/**
 * Ownership math across art variants of a base card.
 *
 * Collection quantities are keyed by base image (`quantitiesByBase[cardImageId]`),
 * each entry describing one owned art variant. These helpers are the single home
 * for the "how many do I own, counting variants?" reduces that grids, detail
 * pages, and deck views all need.
 */

export interface VariantQuantityEntry {
  cardId: number
  quantity: number
}

/** Total copies owned across every art variant of a base card. */
export function totalOwnedAcrossVariants(entries: readonly VariantQuantityEntry[] | undefined): number {
  if (!entries) return 0
  return entries.reduce((sum, entry) => sum + entry.quantity, 0)
}

/** Copies owned of one exact variant (by card id). */
export function ownedOfExactVariant(entries: readonly VariantQuantityEntry[] | undefined, cardId: number): number {
  return entries?.find((entry) => entry.cardId === cardId)?.quantity ?? 0
}

/**
 * Copies owned across every art variant of a base card, with `liveQuantities`
 * (the optimistic per-card quantity map) overlaid on the by-base snapshot. The
 * by-base query has no optimistic stub, so without the overlay a sibling
 * variant's total would lag an add/remove until the refetch lands. Falls back
 * to the plain snapshot sum while the live map is empty (not loaded yet).
 */
export function liveTotalAcrossVariants(
  entries: readonly VariantQuantityEntry[] | undefined,
  liveQuantities: Readonly<Record<number, number>>,
  cardId: number,
): number {
  if (Object.keys(liveQuantities).length === 0) return totalOwnedAcrossVariants(entries)
  const others = (entries ?? []).filter((entry) => entry.cardId !== cardId)
  return others.reduce((sum, entry) => sum + (liveQuantities[entry.cardId] ?? 0), liveQuantities[cardId] ?? 0)
}

/** Owned-quantity label: the combined total across variants, identical on every variant. */
export function ownedQtyLabel(totalAcrossVariants: number): string {
  return `${totalAcrossVariants}x`
}
